import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import Navbar from '@/components/Navbar';
import { TrustMini } from '@/components/TrustBadges';
import { PaperPlaneRight, ShieldCheck, ChatCircle, WarningCircle, WifiHigh, WifiSlash, PushPin, Bell, BellSlash, Check, Checks, FolderSimple, CaretDown, CaretLeft, X } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Prepend the backend host only for a root-relative uploaded avatar; leave an
// absolute Google avatar URL untouched (prepending would break it).
const avatarSrc = (u) => (u ? (u.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL || ''}${u}` : u) : null);

function getWsUrl(token) {
  const base = process.env.REACT_APP_BACKEND_URL || '';
  const wsBase = base.replace(/^https/, 'wss').replace(/^http/, 'ws');
  return `${wsBase}/ws/chat/${token}`;
}

export default function MessagesPage() {
  const { userId: recipientId } = useParams();
  const [searchParams] = useSearchParams();
  const { user, token } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [events, setEvents] = useState([]);
  const [eventMenuOpen, setEventMenuOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [newMsg, setNewMsg] = useState('');
  const [activeConv, setActiveConv] = useState(recipientId || null);
  const [wsConnected, setWsConnected] = useState(false);
  const bottomRef = useRef(null);
  const wsRef = useRef(null);
  const activeConvRef = useRef(activeConv);
  useEffect(() => { activeConvRef.current = activeConv; }, [activeConv]);

  // Fetch conversations
  const fetchConversations = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/messages`, { headers: { Authorization: `Bearer ${token}` } });
      setConversations(res.data);
    } catch (err) { console.error('Failed to load conversations:', err); }
  }, [token]);

  // Tell the server (and the other person) we've read this conversation
  const markRead = useCallback(async (uid) => {
    try {
      await axios.put(`${API}/messages/${uid}/read`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setConversations(prev => prev.map(c => c.user_id === uid ? { ...c, unread: 0 } : c));
    } catch (err) { console.error('Mark read failed:', err); }
  }, [token]);

  // Fetch message history
  const fetchMessages = useCallback(async (uid) => {
    try {
      const res = await axios.get(`${API}/messages/${uid}`, { headers: { Authorization: `Bearer ${token}` } });
      // Guard against a fetch race: if the user switched conversations while this
      // request was in flight, a late (older) response must NOT overwrite the
      // now-active conversation's messages or mark the wrong one read.
      if (activeConvRef.current !== uid) return;
      setMessages(res.data);
      markRead(uid);
      setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    } catch (err) { console.error('Failed to load messages:', err); }
  }, [token, markRead]);

  // Pin / mute / link-to-event for a conversation
  const setPrefs = useCallback(async (uid, prefs) => {
    try {
      await axios.put(`${API}/conversations/${uid}/prefs`, prefs, { headers: { Authorization: `Bearer ${token}` } });
      fetchConversations();
    } catch (err) { console.error('Prefs update failed:', err); }
  }, [token, fetchConversations]);

  // Events (for grouping conversations by what you're planning)
  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/events`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setEvents(r.data)).catch(err => console.error('Events load failed:', err));
  }, [token]);

  // Arriving from the planner with ?event= — link this conversation to that event
  const autoTagged = useRef(false);
  useEffect(() => {
    const evParam = searchParams.get('event');
    if (evParam && recipientId && token && !autoTagged.current) {
      autoTagged.current = true;
      setPrefs(recipientId, { event_id: evParam });
    }
  }, [searchParams, recipientId, token, setPrefs]);

  // WebSocket connection
  useEffect(() => {
    if (!token) return;
    const wsUrl = getWsUrl(token);
    let ws;
    let retryTimeout;

    const connect = () => {
      ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => setWsConnected(true);
      ws.onclose = () => {
        setWsConnected(false);
        retryTimeout = setTimeout(connect, 3000);
      };
      ws.onerror = () => ws.close();
      ws.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data);
          // The other person read our messages — flip our bubbles to "read"
          if (msg.type === 'read') {
            if (msg.reader_id === activeConvRef.current) {
              setMessages(prev => prev.map(m => m.recipient_id === msg.reader_id ? { ...m, read: true } : m));
            }
            return;
          }
          const active = activeConvRef.current;
          const inActive = active && (msg.sender_id === active || msg.recipient_id === active);
          if (inActive) {
            setMessages(prev => {
              if (prev.some(m => m.message_id === msg.message_id)) return prev;
              return [...prev, msg];
            });
            // We're looking at this conversation, so it's read immediately
            if (msg.sender_id === active) {
              axios.put(`${API}/messages/${active}/read`, {}, { headers: { Authorization: `Bearer ${token}` } }).catch(() => {});
            }
            setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
          }
          fetchConversations();
        } catch (err) { console.error('WebSocket message parse error:', err); }
      };
    };

    connect();
    return () => {
      clearTimeout(retryTimeout);
      if (ws) ws.close();
    };
  }, [token, fetchConversations]);

  useEffect(() => { fetchConversations(); }, [fetchConversations]);
  useEffect(() => { if (activeConv) fetchMessages(activeConv); }, [activeConv, fetchMessages]);
  useEffect(() => { setEventMenuOpen(false); }, [activeConv]);

  const [blocked, setBlocked] = useState('');

  // Client-side contact info detection
  const detectContactInfo = (text) => {
    // Phone numbers: 3+ consecutive digits, common formats
    if (/\d{3,}/.test(text.replace(/\s/g, ''))) return 'phone number';
    if (/\(\d{3}\)/.test(text)) return 'phone number';
    if (/\d[\s.\-]?\d[\s.\-]?\d/.test(text) && /\d{7,}/.test(text.replace(/\D/g, ''))) return 'phone number';
    // Email
    if (/[A-Za-z0-9._%+\-]+@[A-Za-z0-9.\-]+\.[A-Za-z]{2,}/.test(text)) return 'email address';
    // Social handles
    if (/@\w{2,}/.test(text)) return 'social handle';
    // Common phrases
    if (/\b(text me|call me|dm me|email me|whatsapp|telegram|instagram)\b/i.test(text)) return 'off-platform contact';
    return '';
  };

  const handleInputChange = (val) => {
    setNewMsg(val);
    const detected = detectContactInfo(val);
    setBlocked(detected);
  };

  const sendMessage = (e) => {
    e.preventDefault();
    if (!newMsg.trim() || !activeConv) return;
    if (blocked) return; // Don't send if contact info detected

    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({ recipient_id: activeConv, body: newMsg, booking_id: null }));
      setNewMsg('');
    } else {
      // Fallback to HTTP
      axios.post(`${API}/messages`, { recipient_id: activeConv, body: newMsg }, { headers: { Authorization: `Bearer ${token}` } })
        .then(() => { setNewMsg(''); fetchMessages(activeConv); fetchConversations(); })
        .catch(() => {});
    }
  };

  const activeUser = conversations.find(c => c.user_id === activeConv);

  const renderConv = (conv) => (
    <div key={conv.user_id} role="button" tabIndex={0} onClick={() => setActiveConv(conv.user_id)}
      onKeyDown={e => { if (e.key === 'Enter') setActiveConv(conv.user_id); }}
      className={`group w-full text-left p-3 min-h-[44px] rounded-xl transition-colors cursor-pointer ${activeConv === conv.user_id ? 'bg-secondary' : 'hover:bg-secondary/50'}`}
      data-testid={`conversation-${conv.user_id}`}>
      <div className="flex items-center gap-3">
        <div className="relative w-9 h-9 bg-secondary rounded-full flex items-center justify-center shrink-0 border border-border/70">
          {conv.user?.avatar_url
            ? <img src={avatarSrc(conv.user.avatar_url)} alt="" className="w-full h-full rounded-full object-cover" />
            : <span className="text-sm font-medium text-primary">{conv.user?.name?.[0] || '?'}</span>}
          {conv.unread > 0 && !conv.muted && (
            <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 bg-accent-coral text-primary-foreground text-[10px] font-bold rounded-full flex items-center justify-center" data-testid={`unread-${conv.user_id}`}>
              {conv.unread > 9 ? '9+' : conv.unread}
            </span>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <p className={`text-sm truncate ${conv.unread > 0 ? 'font-semibold' : 'font-medium'} ${activeConv === conv.user_id ? 'text-primary' : 'text-foreground'}`}>{conv.user?.name || 'User'}</p>
            {conv.pinned && <PushPin size={11} weight="fill" className="text-primary shrink-0" />}
            {conv.muted && <BellSlash size={11} className="text-muted-foreground shrink-0" />}
          </div>
          <p className={`text-[11px] truncate ${conv.unread > 0 ? 'text-foreground font-medium' : 'text-muted-foreground'}`}>{conv.last_message}</p>
        </div>
        <div className="flex flex-col gap-1 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
          <button onClick={e => { e.stopPropagation(); setPrefs(conv.user_id, { pinned: !conv.pinned }); }}
            className="w-6 h-6 rounded-md hover:bg-card flex items-center justify-center cursor-pointer"
            aria-label={conv.pinned ? 'Unpin conversation' : 'Pin conversation'} data-testid={`pin-${conv.user_id}`}>
            <PushPin size={12} weight={conv.pinned ? 'fill' : 'regular'} className={conv.pinned ? 'text-primary' : 'text-muted-foreground'} />
          </button>
          <button onClick={e => { e.stopPropagation(); setPrefs(conv.user_id, { muted: !conv.muted }); }}
            className="w-6 h-6 rounded-md hover:bg-card flex items-center justify-center cursor-pointer"
            aria-label={conv.muted ? 'Unmute notifications' : 'Mute notifications'} data-testid={`mute-${conv.user_id}`}>
            {conv.muted ? <BellSlash size={12} className="text-muted-foreground" /> : <Bell size={12} className="text-muted-foreground" />}
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-background" data-testid="messages-page">
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-8">
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-3xl font-display font-semibold tracking-tight text-foreground">Messages</h1>
          <div className="flex items-center gap-4">
            <TrustMini />
            <div className="flex items-center gap-1.5">
              {wsConnected ? <WifiHigh size={14} className="text-emerald-500" /> : <WifiSlash size={14} className="text-muted-foreground" />}
              <span className="text-[10px] text-muted-foreground">{wsConnected ? 'Live' : 'Connecting...'}</span>
            </div>
          </div>
        </div>

        <div className="bg-card border border-border/70 grid md:grid-cols-3 overflow-hidden rounded-2xl shadow-soft" style={{ height: 'calc(100vh - 200px)' }}>
          {/* Conversations — on mobile, hidden while a chat is open */}
          <div className={`border-r border-border overflow-y-auto p-2 space-y-1 ${activeConv ? 'hidden md:block' : ''}`} data-testid="conversations-list">
            {conversations.length === 0 && !recipientId ? (
              <div className="p-8 text-center">
                <div className="w-10 h-10 bg-secondary rounded-full flex items-center justify-center mx-auto mb-2">
                  <ChatCircle size={18} className="text-primary" />
                </div>
                <p className="text-xs text-muted-foreground">No conversations yet</p>
              </div>
            ) : (
              <>
                {/* Event sections — who you're texting for each event */}
                {events.map(ev => {
                  const evConvs = conversations.filter(c => c.event_id === ev.event_id);
                  if (!evConvs.length) return null;
                  return (
                    <div key={ev.event_id} data-testid={`msg-event-${ev.event_id}`}>
                      <div className="flex items-center gap-1.5 px-2.5 pt-3 pb-1">
                        <FolderSimple size={12} weight="fill" className="text-primary" />
                        <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold truncate">{ev.name}</p>
                        <span className="text-[9px] text-muted-foreground">{evConvs.length}</span>
                      </div>
                      {evConvs.map(renderConv)}
                    </div>
                  );
                })}
                {/* Everything else */}
                {(() => {
                  const evIds = new Set(events.map(e => e.event_id));
                  const loose = conversations.filter(c => !c.event_id || !evIds.has(c.event_id));
                  if (!loose.length) return null;
                  const hasSections = conversations.some(c => c.event_id && evIds.has(c.event_id));
                  return (
                    <>
                      {hasSections && <p className="px-2.5 pt-3 pb-1 text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Other messages</p>}
                      {loose.map(renderConv)}
                    </>
                  );
                })()}
              </>
            )}
          </div>

          {/* Chat — on mobile, hidden until a chat is opened */}
          <div className={`md:col-span-2 flex-col min-h-0 ${activeConv ? 'flex' : 'hidden md:flex'}`}>
            {!activeConv ? (
              <div className="flex-1 flex items-center justify-center text-muted-foreground">
                <div className="text-center fade-up">
                  <div className="w-12 h-12 bg-secondary rounded-full flex items-center justify-center mx-auto mb-3">
                    <ChatCircle size={22} className="text-primary" />
                  </div>
                  <p className="text-sm">Select a conversation</p>
                </div>
              </div>
            ) : (
              <>
                {/* Header */}
                <div className="border-b border-border px-5 py-3 flex items-center justify-between shrink-0 bg-card">
                  <div className="flex items-center gap-3">
                    <button onClick={() => setActiveConv(null)} aria-label="Back to conversations"
                      className="md:hidden w-9 h-9 -ml-2 flex items-center justify-center rounded-lg text-muted-foreground hover:text-primary hover:bg-secondary/60 btn-anim cursor-pointer"
                      data-testid="chat-back-button">
                      <CaretLeft size={18} weight="bold" />
                    </button>
                    <div className="w-8 h-8 bg-secondary rounded-full flex items-center justify-center border border-border/70">
                      <span className="text-xs font-medium text-primary">{activeUser?.user?.name?.[0] || '?'}</span>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground">{activeUser?.user?.name || 'Provider'}</p>
                      <p className="text-[11px] text-muted-foreground">{activeUser?.muted ? 'Notifications off' : 'Notifications on'}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {/* Link conversation to an event */}
                    <div className="relative">
                      <button onClick={() => setEventMenuOpen(o => !o)}
                        className={`h-8 px-2.5 rounded-lg flex items-center gap-1.5 text-[11px] font-medium btn-anim cursor-pointer ${activeUser?.event_id ? 'bg-secondary text-primary' : 'text-muted-foreground hover:bg-secondary/60'}`}
                        data-testid="chat-event-button">
                        <FolderSimple size={13} weight={activeUser?.event_id ? 'fill' : 'regular'} />
                        <span className="max-w-[110px] truncate hidden sm:inline">
                          {events.find(e => e.event_id === activeUser?.event_id)?.name || 'Link to event'}
                        </span>
                        <CaretDown size={9} />
                      </button>
                      {eventMenuOpen && (
                        <div className="absolute right-0 top-9 z-50 w-48 bg-card border border-border/70 shadow-float py-1 rounded-xl" data-testid="chat-event-menu">
                          {events.length === 0 && <p className="px-3 py-2 text-[11px] text-muted-foreground">No events yet — create one in the AI Planner.</p>}
                          {events.map(ev => (
                            <button key={ev.event_id} onClick={() => { setPrefs(activeConv, { event_id: ev.event_id }); setEventMenuOpen(false); }}
                              className="w-full flex items-center gap-2 px-3 py-1.5 text-[11px] text-foreground hover:bg-secondary/60 cursor-pointer">
                              <FolderSimple size={12} className={ev.event_id === activeUser?.event_id ? 'text-primary' : 'text-muted-foreground'} weight={ev.event_id === activeUser?.event_id ? 'fill' : 'regular'} />
                              <span className="truncate">{ev.name}</span>
                              {ev.event_id === activeUser?.event_id && <Check size={11} className="text-primary ml-auto shrink-0" />}
                            </button>
                          ))}
                          {activeUser?.event_id && (
                            <>
                              <div className="my-0.5 border-t border-border/70" />
                              <button onClick={() => { setPrefs(activeConv, { event_id: '' }); setEventMenuOpen(false); }}
                                className="w-full flex items-center gap-2 px-3 py-1.5 text-[11px] text-foreground hover:bg-secondary/60 cursor-pointer">
                                <X size={12} className="text-muted-foreground" /> Remove from event
                              </button>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                    <button onClick={() => setPrefs(activeConv, { pinned: !activeUser?.pinned })}
                      className={`w-8 h-8 rounded-lg flex items-center justify-center btn-anim cursor-pointer ${activeUser?.pinned ? 'bg-secondary text-primary' : 'text-muted-foreground hover:bg-secondary/60'}`}
                      aria-label={activeUser?.pinned ? 'Unpin conversation' : 'Pin conversation'} data-testid="chat-pin-button">
                      <PushPin size={14} weight={activeUser?.pinned ? 'fill' : 'regular'} />
                    </button>
                    <button onClick={() => setPrefs(activeConv, { muted: !activeUser?.muted })}
                      className={`w-8 h-8 rounded-lg flex items-center justify-center btn-anim cursor-pointer ${activeUser?.muted ? 'bg-secondary text-muted-foreground' : 'text-muted-foreground hover:bg-secondary/60'}`}
                      aria-label={activeUser?.muted ? 'Unmute notifications' : 'Mute notifications'} data-testid="chat-mute-button">
                      {activeUser?.muted ? <BellSlash size={14} /> : <Bell size={14} />}
                    </button>
                    <div className="flex items-center gap-1.5 ml-1">
                      {wsConnected ? <WifiHigh size={12} className="text-emerald-500" /> : <WifiSlash size={12} className="text-muted-foreground/60" />}
                      <span className="text-[10px] text-muted-foreground">{wsConnected ? 'Real-time' : 'Polling'}</span>
                    </div>
                  </div>
                </div>

                {/* Warning */}
                <div className="border-b border-border px-5 py-2.5 bg-secondary/60 flex items-center gap-2 shrink-0">
                  <ShieldCheck size={14} weight="fill" className="text-primary shrink-0" />
                  <span className="text-[11px] text-muted-foreground">Phone numbers and contact info are hidden until booking is confirmed — for your protection.</span>
                </div>

                {/* Messages */}
                <div className="flex-1 min-h-0 overflow-y-auto p-5 space-y-3 bg-background/50" data-testid="messages-area">
                  {messages.map(msg => (
                    <div key={msg.message_id} className={`flex fade-up ${msg.sender_id === user?.user_id ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[70%] px-4 py-2.5 text-sm ${
                        msg.sender_id === user?.user_id
                          ? 'bg-primary text-primary-foreground rounded-2xl rounded-br-sm shadow-soft'
                          : 'bg-card text-foreground border border-border/70 rounded-2xl rounded-bl-sm shadow-soft'
                      } ${msg.blocked_for_contact_sharing ? 'opacity-60 italic' : ''}`}>
                        {msg.body}
                        <div className={`text-[9px] mt-1 flex items-center gap-1 ${msg.sender_id === user?.user_id ? 'text-primary-foreground/60 justify-end' : 'text-muted-foreground'}`}>
                          {msg.created_at ? new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}
                          {msg.sender_id === user?.user_id && (
                            msg.read
                              ? <Checks size={11} weight="bold" className="text-primary-foreground" data-testid={`read-${msg.message_id}`} />
                              : <Check size={11} weight="bold" className="text-primary-foreground/50" data-testid={`sent-${msg.message_id}`} />
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                  <div ref={bottomRef} />
                </div>

                {/* Input */}
                <div className="border-t border-border bg-card shrink-0">
                  {blocked && (
                    <div className="px-4 pt-2.5 flex items-center gap-2" data-testid="contact-blocked-warning">
                      <div className="w-4 h-4 bg-red-100 rounded-full flex items-center justify-center shrink-0">
                        <WarningCircle size={12} weight="fill" className="text-red-500" />
                      </div>
                      <p className="text-[11px] text-red-600">
                        <span className="font-semibold">Can't send:</span> {blocked} detected. Contact info is blocked until booking is confirmed — for your protection.
                      </p>
                    </div>
                  )}
                  <form onSubmit={sendMessage} className="p-3 flex items-center gap-2" data-testid="message-form">
                    <input type="text" value={newMsg} onChange={e => handleInputChange(e.target.value)} placeholder="Type a message..."
                      className={`flex-1 min-w-0 bg-background border px-4 py-3 min-h-[44px] text-sm rounded-full focus:outline-none focus:ring-2 placeholder:text-muted-foreground/70 text-foreground transition-shadow ${blocked ? 'border-red-300 focus:ring-red-400 bg-red-50/30' : 'border-border focus:ring-primary/40'}`} data-testid="message-input" />
                    <button type="submit" disabled={!newMsg.trim() || !!blocked}
                      className="bg-primary text-primary-foreground w-11 h-11 rounded-full flex items-center justify-center hover:bg-primary/90 hover:shadow-lift btn-anim cursor-pointer disabled:opacity-30 transition-colors shrink-0 shadow-soft" aria-label="Send message" data-testid="send-message-button">
                      <PaperPlaneRight size={16} weight="fill" />
                    </button>
                  </form>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
